import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { cancelReceipt, getAllCooReceipts } from "../../../../../apis/api";
import { Dialog } from "primereact/dialog";
import { RePrintInvoice } from "./RePrintInvoice";
import { useReactToPrint } from "react-to-print";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import moment from "moment";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";

export const Receipts = () => {
  const userType = window.localStorage.getItem("userType");
  const invoiceRef = useRef(null);
  const toast = useRef(null);

  const handleMemberPrint = useReactToPrint({
    content: () => invoiceRef.current,
  });

  const [data, setData] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [singleReceipt, setSingleReceipt] = useState({});
  const [displayBasic2, setDisplayBasic2] = useState(false);
  const [displayFilterDialog, setDisplayFilterDialog] = useState(false);
  const [update, setUpdate] = useState(false);
  const [loading, setLoading] = useState(false);

  // Filter states - separate from applied filters
  const [tempFilters, setTempFilters] = useState({
    startDate: null,
    endDate: null,
    status: null,
    receiptNo: "",
    companyName: "",
    cooType: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    startDate: null,
    endDate: null,
    status: null,
    receiptNo: "",
    companyName: "",
    cooType: "",
  });

  const statusOptions = [
    { label: "All", value: null },
    { label: "Active", value: "active" },
    { label: "Cancelled", value: "cancelled" },
  ];

  const cooTypeOptions = [
    { label: "All", value: "" },
    { label: "Attestation", value: "Attestation" },
    { label: "Visa Letter", value: "Visa Letter" },
    { label: "Coo", value: "COO" },
  ];

  // ================= FETCH DATA =================
  const fetchData = useCallback(async () => {
    setLoading(true);

    const pageNumber = Math.floor(first / rows) + 1;

    const params = {
      pageNumber,
      pageSize: rows,
    };

    // Only use applied filters, not temp filters
    if (appliedFilters.startDate)
      params.startDate = moment(appliedFilters.startDate).format("YYYY-MM-DD");
    if (appliedFilters.endDate)
      params.endDate = moment(appliedFilters.endDate).format("YYYY-MM-DD");
    if (appliedFilters.status) params.status = appliedFilters.status;
    if (appliedFilters.companyName) params.companyName = appliedFilters.companyName;
    if (appliedFilters.receiptNo) params.receiptNo = appliedFilters.receiptNo;
    if (appliedFilters.cooType) params.cooType = appliedFilters.cooType;

    try {
      const res = await getAllCooReceipts(params);
      setData(res.data?.results || []);
      setTotalRecords(res.data?.total || 0);
    } catch (err) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch receipts",
      });
    } finally {
      setLoading(false);
    }
  }, [first, rows, appliedFilters, update]); // Changed from filters to appliedFilters

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ================= PAGINATION =================
  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  // ================= FILTER FUNCTIONS =================
  const clearFilters = () => {
    const clearedFilters = {
      startDate: null,
      endDate: null,
      status: null,
      receiptNo: "",
      companyName: "",
      cooType: "",
    };
    
    // Clear both temp and applied filters
    setTempFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setFirst(0);
    setDisplayFilterDialog(false);
  };

  const applyFilters = () => {
    // Apply temp filters to applied filters
    setAppliedFilters(tempFilters);
    setFirst(0); // Reset to first page
    setDisplayFilterDialog(false);
  };

  const handleFilterChange = (key, value) => {
    setTempFilters((prev) => ({ ...prev, [key]: value }));
  };

  const openFilterDialog = () => {
    // Copy applied filters to temp filters when opening dialog
    setTempFilters({ ...appliedFilters });
    setDisplayFilterDialog(true);
  };

  // ================= EXPORT TO EXCEL =================
  const exportToExcel = () => {
    if (data.length === 0) {
      toast.current.show({
        severity: "warn",
        summary: "No Data",
        detail: "No data to export",
      });
      return;
    }

    const exportData = data.map((item, index) => ({
      "S.No": index + 1,
      "Receipt No": item.receipt_no || "",
      "Organization": item.member_name_of_org || "",
      "COO Type": item.coo_type || "",
      "No of COO": item.no_of_coo || 0,
      "Date": moment(item.created_at).format("DD-MM-YYYY"),
      "Status": item.cancel_status ? "Cancelled" : "Active",
      "Payment Mode": item.pay_mode || "",
      "Amount": item.total_amount || item.amount || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "COO Receipts");
    
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { 
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
    });
    
    saveAs(dataBlob, `COO_Receipts_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`);
  };

  // ================= UI HELPERS =================
  const receiptNo = (row) => (
    <span
      style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
      onClick={() => {
        setSingleReceipt(row);
        setDisplayBasic2(true);
      }}
    >
      {row.receipt_no}
    </span>
  );

  const cancelStatus = (row) =>
    row.cancel_status ? (
      <Tag severity="danger" value="Cancelled" />
    ) : (
      <Tag severity="success" value="Active" />
    );

  const createdAt = (row) =>
    moment(row.created_at).format("DD-MM-YYYY");

  // ================= CANCEL RECEIPT =================
  const cancelCooReceipt = async (receipt) => {
    try {
      await cancelReceipt(receipt.id);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Receipt cancelled successfully",
      });
      setDisplayBasic2(false);
      setUpdate((p) => !p);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to cancel receipt",
      });
    }
  };

  // ================= FILTER DIALOG =================
  const renderFilterDialog = () => (
    <Dialog
      header="Filter Receipts"
      visible={displayFilterDialog}
      style={{ width: "50vw" }}
      onHide={() => setDisplayFilterDialog(false)}
    >
      <div className="p-fluid grid">
        <div className="field col-12 md:col-6">
          <label htmlFor="startDate">Start Date</label>
          <Calendar
            id="startDate"
            value={tempFilters.startDate}
            onChange={(e) => handleFilterChange("startDate", e.value)}
            dateFormat="dd/mm/yy"
            showIcon
          />
        </div>

        <div className="field col-12 md:col-6">
          <label htmlFor="endDate">End Date</label>
          <Calendar
            id="endDate"
            value={tempFilters.endDate}
            onChange={(e) => handleFilterChange("endDate", e.value)}
            dateFormat="dd/mm/yy"
            showIcon
          />
        </div>

        <div className="field col-12 md:col-6">
          <label htmlFor="status">Status</label>
          <Dropdown
            id="status"
            value={tempFilters.status}
            options={statusOptions}
            onChange={(e) => handleFilterChange("status", e.value)}
            placeholder="Select Status"
          />
        </div>

        <div className="field col-12 md:col-6">
          <label htmlFor="cooType">COO Type</label>
          <Dropdown
            id="cooType"
            value={tempFilters.cooType}
            options={cooTypeOptions}
            onChange={(e) => handleFilterChange("cooType", e.value)}
            placeholder="Select COO Type"
          />
        </div>

        <div className="field col-12 md:col-6">
          <label htmlFor="receiptNo">Receipt No</label>
          <InputText
            id="receiptNo"
            value={tempFilters.receiptNo}
            onChange={(e) => handleFilterChange("receiptNo", e.target.value)}
            placeholder="Enter receipt number"
          />
        </div>

        <div className="field col-12 md:col-6">
          <label htmlFor="companyName">Company Name</label>
          <InputText
            id="companyName"
            value={tempFilters.companyName}
            onChange={(e) => handleFilterChange("companyName", e.target.value)}
            placeholder="Enter company name"
          />
        </div>
      </div>

      <div className="flex justify-content-end gap-2 mt-4">
        <Button
          label="Clear All"
          icon="pi pi-trash"
          className="p-button-outlined p-button-secondary"
          onClick={clearFilters}
        />
        <Button
          label="Apply Filters"
          icon="pi pi-check"
          onClick={applyFilters}
        />
      </div>
    </Dialog>
  );

  // ================= ACTIVE FILTERS BADGES =================
  const renderActiveFilters = () => {
    const activeFilters = [];
    
    if (appliedFilters.startDate) {
      activeFilters.push(`From: ${moment(appliedFilters.startDate).format("DD/MM/YYYY")}`);
    }
    if (appliedFilters.endDate) {
      activeFilters.push(`To: ${moment(appliedFilters.endDate).format("DD/MM/YYYY")}`);
    }
    if (appliedFilters.status) {
      const statusLabel = statusOptions.find(opt => opt.value === appliedFilters.status)?.label;
      activeFilters.push(`Status: ${statusLabel}`);
    }
    if (appliedFilters.cooType) {
      const typeLabel = cooTypeOptions.find(opt => opt.value === appliedFilters.cooType)?.label;
      activeFilters.push(`Type: ${typeLabel}`);
    }
    if (appliedFilters.receiptNo) {
      activeFilters.push(`Receipt: ${appliedFilters.receiptNo}`);
    }
    if (appliedFilters.companyName) {
      activeFilters.push(`Company: ${appliedFilters.companyName}`);
    }

    if (activeFilters.length === 0) return null;

    return (
      <div className="mb-3">
        <div className="flex align-items-center gap-2 mb-2">
          <i className="pi pi-filter" style={{ fontSize: "0.875rem" }} />
          <span className="font-bold">Active Filters:</span>
          <Button
            label="Clear All"
            icon="pi pi-times"
            className="p-button-text p-button-sm"
            onClick={clearFilters}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {activeFilters.map((filter, index) => (
            <Tag
              key={index}
              value={filter}
              rounded
              icon="pi pi-filter"
              className="bg-blue-100 text-blue-800"
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="m-3">
      <Toast ref={toast} />
      {renderFilterDialog()}
      
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full bg-white bg-opacity-80 flex align-items-center justify-content-center z-5">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem", color: '#000000ff' }} />
        </div>
      )}
      
      <Card className="shadow-1">
        <div className="flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="m-0">COO Receipts</h3>
            <p className="text-color-secondary m-0 mt-1">
              Showing {first + 1} to {Math.min(first + rows, totalRecords)} of {totalRecords} records
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              label="Filter"
              icon="pi pi-filter"
              className="p-button-outlined"
              onClick={openFilterDialog}
              badge={Object.values(appliedFilters).filter(v => v && v !== "").length}
              badgeClassName="p-badge-danger"
            />
            <Button
              label="Export"
              icon="pi pi-file-excel"
              className="p-button-outlined p-button-success"
              onClick={exportToExcel}
            />
          </div>
        </div>

        {renderActiveFilters()}

        <DataTable
          value={data}
          lazy
          showGridlines
          size="small"
          emptyMessage="No receipts found"
          className="p-datatable-sm"
          paginator={false}
        >
          <Column field="receipt_no" header="Invoice No" body={receiptNo} style={{ minWidth: '120px' }} />
          <Column field="member_name_of_org" header="Organization" style={{ minWidth: '180px' }} />
          <Column field="coo_type" header="COO Type" style={{ minWidth: '120px' }} />
          <Column field="no_of_coo" header="No of COO" style={{ minWidth: '100px' }} />
          <Column header="Date" body={createdAt} style={{ minWidth: '120px' }} />
          <Column header="Status" body={cancelStatus} style={{ minWidth: '100px' }} />
          <Column field="pay_mode" header="Payment Mode" style={{ minWidth: '120px' }} />
          <Column
            header="Amount"
            body={(r) => `₹${(r.total_amount || r.amount || 0).toLocaleString('en-IN')}`}
            style={{ minWidth: '120px' }}
          />
        </DataTable>

        <Paginator
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          className="mt-3 border-top-1"
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        />
      </Card>

      <Dialog
        header="Receipt Details"
        visible={displayBasic2}
        style={{ width: "60vw" }}
        onHide={() => setDisplayBasic2(false)}
        footer={
          <div>
            <Button
              label="Reprint"
              icon="pi pi-print"
              onClick={handleMemberPrint}
              className="mr-2"
            />
            {userType === "Account Team" && !singleReceipt.cancel_status && (
              <Button
                label="Cancel Receipt"
                icon="pi pi-times"
                className="p-button-danger"
                onClick={() => cancelCooReceipt(singleReceipt)}
              />
            )}
          </div>
        }
      >
        <div ref={invoiceRef}>
          <RePrintInvoice data={singleReceipt} isDuplicate={false} />
          <RePrintInvoice data={singleReceipt} isDuplicate={true} />
        </div>
      </Dialog>
    </div>
  );
};